import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, TablePagination, Typography } from '@mui/material';
import moment from 'moment';
import { t } from 'i18next';
import POSTable, { RowType } from '@/components/POS/Common/POSTable';
import POSButton from '@/components/POS/Common/POSButton';
import { formatCurrency } from '@/scenes/POS/Core/pos.utils';
import {
    GetApiDirectWebstorePayments200DataItem,
    GetApiDirectWebstorePayments200DataItemRefundStatus,
    GetApiDirectWebstorePayments200DataItemType,
} from '@/shared/api/models';
import { useDirectWebstorePayments } from '@/hooks/api/pos/onlinePayments';
import RefundPaymentModal from '../Modals/RefundPaymentModal';
import PaymentTransactionsDrawer from '../Drawer/PaymentTransactionsDrawer';
import Permission from '@/utils/POS/Permission';

const getPaymentTypeLabel = (type: GetApiDirectWebstorePayments200DataItem['type']) => {
    if (type === GetApiDirectWebstorePayments200DataItemType.GIFT_CARD) {
        return t('POS.GiftCard');
    }
    if (type === GetApiDirectWebstorePayments200DataItemType.DEPOSIT) {
        return t('POS.DepositPayment');
    }
    return type;
};

const canRefund = (status: GetApiDirectWebstorePayments200DataItem['refund_status']) =>
    status === GetApiDirectWebstorePayments200DataItemRefundStatus.PAID ||
    status === GetApiDirectWebstorePayments200DataItemRefundStatus.PARTIALLY_REFUNDED;

export default function OnlinePaymentsList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [refundPayment, setRefundPayment] = useState<GetApiDirectWebstorePayments200DataItem | null>(null);
    const [transactionsSalesId, setTransactionsSalesId] = useState<string | null>(null);
    const { isAllowed } = Permission();
    const haveReadInvoicePermission = isAllowed('Invoice', 'read');

    const { data, isFetching, refetch } = useDirectWebstorePayments({
        params: {
            page: page + 1,
            limit: rowsPerPage,
        },
    });

    const payments = data?.data ?? [];
    const total = data?.total ?? 0;

    const columns = [
        {
            id: 'date',
            name: t('Common.Date'),
            selector: (row: RowType) => moment(row.date).format('DD/MM-YYYY HH:mm'),
            sortable: false,
        },
        {
            id: 'type',
            name: t('Integration.Type'),
            selector: (row: RowType) => getPaymentTypeLabel(row.type),
            sortable: false,
        },
        {
            id: 'customerName',
            name: t('Insights.Customer'),
            selector: (row: RowType) =>
                row.customer?.id ? (
                    <Typography
                        component={Link}
                        to={`/customers/${row.customer?.id}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: '#1976d2', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {row.customer?.customer_name}
                    </Typography>
                ) : (
                    <Typography>{row.customer?.customer_name}</Typography>
                ),
            sortable: false,
        },
        {
            id: 'invoiceNumber',
            name: t('POS.InvoiceNumber'),
            selector: (row: RowType) =>
                haveReadInvoicePermission ? (
                    <Typography
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            window.open(`${process.env.REACT_APP_URL2}/api/invoice/${row.salesId}/pdf`, '_blank');
                        }}
                        sx={{ color: '#1976d2', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {row.invoiceId}
                    </Typography>
                ) : (
                    <Typography>{row.invoiceId}</Typography>
                ),
            sortable: false,
        },
        {
            id: 'amount',
            name: t('POS.TotalAmount'),
            selector: (row: RowType) => formatCurrency(row.amount),
            sortable: false,
        },
        {
            id: 'refundAmount',
            name: t('POS.RefundAmount'),
            selector: (row: RowType) => formatCurrency(row.refund_amount ?? 0),
            sortable: false,
        },
        {
            id: 'action',
            name: ' ',
            selector: (row: RowType) =>
                canRefund(row.refund_status) ? (
                    <POSButton
                        title={t('POS.Refund')}
                        variant="f_outline"
                        onClick={(e) => {
                            e.stopPropagation();
                            setRefundPayment(row as GetApiDirectWebstorePayments200DataItem);
                        }}
                    />
                ) : null,
            sortable: false,
        },
    ];

    return (
        <React.Fragment>
            <POSTable
                columns={columns}
                data={payments}
                loading={isFetching}
                maxHeight="75dvh"
                isServerSorting={false}
                onRowClick={(row) => {
                    setTransactionsSalesId(row.salesId);
                }}
            />

            <Stack alignItems="flex-end">
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Stack>

            <RefundPaymentModal
                open={!!refundPayment}
                onClose={() => setRefundPayment(null)}
                payment={refundPayment}
                onSuccess={() => refetch()}
            />

            <PaymentTransactionsDrawer
                open={!!transactionsSalesId}
                onClose={() => setTransactionsSalesId(null)}
                salesId={transactionsSalesId}
            />
        </React.Fragment>
    );
}
